<script lang="ts">
import { computed, defineComponent, h, ref, unref } from 'vue';

import { classNames } from '@components/utils';

import { createForm, makeFormProps, makeThemeProps, provideTheme, useRender } from '../../composable';
import { CdsInputDescription, CdsInputLabel } from '../CdsInput';

import type { SubmitEventPromise } from '../../composable';

export const componentName = 'CdsForm';
export default defineComponent({
  name: componentName,
  components: { CdsInputLabel, CdsInputDescription },
  props: {
    ...makeFormProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при изменении значения (v-model)
     */
    'update:modelValue': (value: boolean | null) => true,
    /**
     * Событие при отправке формы
     */
    submit: (event: SubmitEventPromise) => true
  },
  setup(props, { emit, slots, expose }) {
    const form = createForm(props);
    const formRef = ref<HTMLFormElement>();
    const { themeClasses } = provideTheme(props);

    const hasLabel = computed(() => !!props.label || !!slots.label);
    const hasDescription = computed(() => !!props.description || !!slots.description);

    function onReset(event: Event) {
      event.preventDefault();
      form.reset();
    }

    function onSubmit(_event: Event) {
      const event = _event as SubmitEventPromise;

      const ready = form.validate();

      event.then = ready.then.bind(ready);
      event.catch = ready.catch.bind(ready);
      event.finally = ready.finally.bind(ready);

      emit('submit', event);

      if (!event.defaultPrevented) {
        ready.then(({ valid }) => {
          if (valid) {
            unref(formRef)?.submit();
          }
        });
      }

      event.preventDefault();
    }

    useRender(() =>
      h(
        'form',
        {
          ref: formRef,
          novalidate: true,
          class: classNames('cds-form', unref(themeClasses)),
          onReset,
          onSubmit
        },
        [
          unref(hasLabel) &&
            h(
              CdsInputLabel,
              {
                tag: 'legend',
                text: props.label,
                class: 'cds-form__label'
              },
              {
                default: slots.label
              }
            ),
          unref(hasDescription) &&
            h(
              CdsInputDescription,
              {
                text: props.description,
                class: 'cds-form__description'
              },
              {
                default: slots.description
              }
            ),
          slots.default?.(form)
        ]
      )
    );

    expose({
      ...form,
      form,
      formRef
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsForm';
</style>
